import withLoading from '@oncolens/oncolens-ui/lib/components/Common/withLoading';
import { SectionTitle } from '@/components/Layout/SectionTitle';
import { EventDto } from '@oncolens/oncolens-ui/lib/dto/event/Event';
import React from 'react';

interface CaseInformationProps {
	eventData: EventDto;
}

export const CaseInformation = ({ eventData }: CaseInformationProps) => {
  return (
    <div className="print:hidden">
      <SectionTitle title="Case Summary" />
      <CaseInformationBodyWithLoading isLoading={!eventData} eventData={eventData} />
    </div>
  );
};

const CaseInformationBody = ({ eventData }: CaseInformationProps) => {
  const primaryDisease = eventData?.diseases?.find((x) => x.isPrimary);
  const primaryEpisode = primaryDisease?.episodes?.find((x) => x.isTargetEpisode);

  return (
    <div className="flex mb-4">
      <dl className="flex-1 mx-3">
        <div>
          <dt className="inline mr-1 font-bold">Age: </dt>
          <dd className="inline" data-cy="case-information-age">
            {eventData?.patient?.age}
          </dd>
        </div>
        {eventData?.patient?.raceConfig && <div>
          <dt className="inline mr-1 font-bold">Race: </dt>
          <dd className="inline" data-cy="case-information-race">
            {eventData?.patient?.raceConfig?.value}
          </dd>
        </div>}
        <div>
          <dt className="inline mr-1 font-bold">Sex: </dt>
          <dd className="inline" data-cy="case-information-sex">
            {eventData?.patient?.sexConfig?.value}
          </dd>
        </div>
      </dl>
      <dl className="flex-1 mx-3">
        <div>
          <dt className="inline mr-1 font-bold">Reason for Request: </dt>
          <dd className="inline" data-cy="case-information-reason">
            {eventData?.topicForDiscussion}
          </dd>
        </div>
      </dl>
      {primaryDisease ? (
        <dl className="flex-1 mx-3">
          <div>
            <dt className="inline mr-1 font-bold">Cancer: </dt>
            <dd className="inline">{primaryDisease?.cancerType?.value}</dd>
          </div>
          <div>
            <dt className="inline mr-1 font-bold">Primary Location: </dt>
            <dd className="inline">{primaryDisease?.cancerLocation?.value}</dd>
          </div>
          <div>
            <dt className="inline mr-1 font-bold">Pathology: </dt>
            <dd className="inline">{primaryDisease?.cancerPathology?.value}</dd>
          </div>
          <div>
            <dt className="inline mr-1 font-bold">Setting: </dt>
            <dd className="inline">{primaryEpisode?.cancerSettingId}</dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
};

const CaseInformationSkeleton = () => (
  <div className="flex mb-4">
    <dl className="flex-1 mx-3">
      <div className="py-0.5">
        <dt className="block h-5 bg-gray-300 rounded-lg w-28 animate-pulse"></dt>
      </div>
      <div className="py-0.5">
        <dt className="block h-5 bg-gray-300 rounded-lg w-36 animate-pulse"></dt>
      </div>
      <div className="py-0.5">
        <dt className="block h-5 bg-gray-300 rounded-lg w-28 animate-pulse"></dt>
      </div>
    </dl>
    <dl className="flex-1 mx-3">
      <div className="py-0.5">
        <dt className="block h-5 bg-gray-300 rounded-lg w-44 animate-pulse"></dt>
      </div>
      <div className="py-0.5">
        <dt className="block w-40 h-5 bg-gray-300 rounded-lg animate-pulse"></dt>
      </div>
      <div className="py-0.5">
        <dt className="block w-48 h-5 bg-gray-300 rounded-lg animate-pulse"></dt>
      </div>
      <div className="py-0.5">
        <dt className="block h-5 bg-gray-300 rounded-lg w-44 animate-pulse"></dt>
      </div>
    </dl>
  </div>
);

const CaseInformationBodyWithLoading = withLoading(CaseInformationBody, CaseInformationSkeleton);
